/**
 * Banned Users Management Component
 * Allows group admins/moderators to view and manage banned users
 */

import { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material'
import {
  Block as BlockIcon,
  Person as PersonIcon,
  RemoveCircle as UnbanIcon,
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { searchApi } from '@api/endpoints/search'
import type { UserSearchResult } from '@api/types/search'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { socialApi } from '@api/endpoints/social'
import type { GroupBannedUserDto } from '@api/types/social'

interface BannedUsersManagementProps {
  groupId: string
  groupName: string
}

export function BannedUsersManagement({ groupId, groupName }: BannedUsersManagementProps) {
  const queryClient = useQueryClient()
  const [selectedUser, setSelectedUser] = useState<GroupBannedUserDto | null>(null)
  const [banDialogOpen, setBanDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([])
  const [selectedUserToBan, setSelectedUserToBan] = useState<UserSearchResult | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  })

  // Fetch banned users
  const {
    data: bannedUsers = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['groupBans', groupId],
    queryFn: () => socialApi.getGroupBans(groupId),
  })

  // Unban mutation
  const unbanMutation = useMutation({
    mutationFn: (userId: string) => socialApi.unbanUser(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupBans', groupId] })
      setSnackbar({
        open: true,
        message: `${selectedUser?.userName} has been unbanned from ${groupName}`,
        severity: 'success'
      })
      setSelectedUser(null)
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to unban user'
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      })
    },
  })

  // Ban mutation
  const banMutation = useMutation({
    mutationFn: (userId: string) => socialApi.banUser(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupBans', groupId] })
      queryClient.invalidateQueries({ queryKey: ['groupMembers', groupId] })
      setSnackbar({
        open: true,
        message: `${selectedUserToBan?.displayName} has been banned from ${groupName}`,
        severity: 'success'
      })
      handleCloseBanDialog()
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to ban user'
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      })
    },
  })

  const handleUnbanClick = (user: GroupBannedUserDto) => {
    setSelectedUser(user)
  }

  const handleConfirmUnban = () => {
    if (selectedUser?.userId) {
      unbanMutation.mutate(selectedUser.userId)
    }
  }

  const handleOpenBanDialog = () => {
    setBanDialogOpen(true)
    setSearchQuery('')
    setSearchResults([])
    setSelectedUserToBan(null)
  }

  const handleCloseBanDialog = () => {
    setBanDialogOpen(false)
    setSearchQuery('')
    setSearchResults([])
    setSelectedUserToBan(null)
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setIsSearching(true)
    try {
      const results = await searchApi.search({
        query: searchQuery,
        type: 2, // SearchEntityType.User
        take: 10,
      })
      setSearchResults(results.users || [])
    } catch (error) {
      console.error('Search failed:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleConfirmBan = () => {
    if (selectedUserToBan?.userId) {
      banMutation.mutate(selectedUserToBan.userId)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown date'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load banned users: {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6">Banned Users</Typography>
        {bannedUsers.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            ({bannedUsers.length})
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Ban User">
          <IconButton color="error" onClick={handleOpenBanDialog}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {bannedUsers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BlockIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            No banned users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Users banned from {groupName} will appear here
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List sx={{ p: 0 }}>
            {bannedUsers.map((user, index) => (
              <Box key={user.userId || index}>
                <ListItem
                  sx={{
                    py: 2,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.userAvatar || undefined}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight="medium">
                        {user.userName || 'Unknown User'}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Banned {formatDate(user.bannedDate)}
                        </Typography>
                        {user.bannedBy && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            by {user.bannedBy}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Tooltip title="Unban User">
                    <IconButton
                      color="success"
                      onClick={() => handleUnbanClick(user)}
                      disabled={unbanMutation.isPending}
                    >
                      <UnbanIcon />
                    </IconButton>
                  </Tooltip>
                </ListItem>
                {index < bannedUsers.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </Paper>
      )}

      {/* Confirm Unban Dialog */}
      <Dialog open={!!selectedUser} onClose={() => setSelectedUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Unban User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar src={selectedUser.userAvatar || undefined} sx={{ width: 48, height: 48 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {selectedUser.userName || 'Unknown User'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Banned {formatDate(selectedUser.bannedDate)}
                  </Typography>
                </Box>
              </Box>

              <Alert severity="info">
                {selectedUser.userName || 'This user'} will be able to rejoin {groupName} after being unbanned.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedUser(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmUnban}
            disabled={unbanMutation.isPending}
          >
            {unbanMutation.isPending ? 'Unbanning...' : 'Unban'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ban User Dialog */}
      <Dialog open={banDialogOpen} onClose={handleCloseBanDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Ban User</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search for a user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={handleSearch}
                      disabled={isSearching || !searchQuery.trim()}
                    >
                      {isSearching ? <CircularProgress size={20} /> : 'Search'}
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {searchResults.length > 0 && (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {searchResults.map((user) => (
                <ListItem
                  key={user.userId}
                  onClick={() => setSelectedUserToBan(user)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: selectedUserToBan?.userId === user.userId ? 'action.selected' : 'transparent',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatar || user.profilePicture || undefined}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.displayName}
                    secondary={user.location || user.homeTown}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {selectedUserToBan && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Ban <strong>{selectedUserToBan.displayName}</strong> from {groupName}?
              They will be removed from the group and unable to rejoin.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBanDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmBan}
            disabled={!selectedUserToBan || banMutation.isPending}
          >
            {banMutation.isPending ? 'Banning...' : 'Ban User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
